import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";
import { accesContenuField, accesContenuInputField } from "./acces.js";

/**
 * Content : famille "contenu edite par une surface Canvas" (un dessin, une
 * carte mentale, un plan de salle). Lot 1 du kit des surfaces Canvas
 * (`docs/CHANTIERS-SOCLE.md`, section `kit-documents`).
 *
 * POURQUOI UNE FAMILLE A PART. Une surface Canvas range un objet de plusieurs
 * centaines de Ko, opaque pour le socle : Firestore n'a pas a l'indexer, une
 * liste n'a pas a le telecharger, et il depasse vite le plafond d'un document
 * Firestore (1 Mio). Aucune famille existante ne tient ces trois contraintes.
 * Une surface qui manipule des contacts ou des reservations continue
 * d'utiliser leurs familles : on n'enfouit pas le metier dans un contenu.
 *
 * DEUX NIVEAUX, et c'est tout le contrat :
 *   - `contents/{id}` : la FICHE du contenu, ses metadonnees seules (nom,
 *     vignette, taille, versions). JAMAIS la charge : une galerie qui liste
 *     cent contenus ne telecharge que leurs fiches (`ContentSchema`) ;
 *   - `contents/{id}/parts/{i}` : les MORCEAUX de la charge, `i` de 0 a
 *     `parts - 1`, chacun `{ version, payload }` (`ContentPartSchema`). Leur
 *     identite est leur chemin : ni parent a declarer, ni clef a choisir.
 *
 * LA COHERENCE EST TENUE PAR LES SECURITY RULES, pas seulement par le SDK
 * (`firebase/firestore.rules`, bloc `contents`) : une fiche dont la `version`
 * monte exige, dans la MEME ecriture groupee, ses `parts` morceaux a cette
 * version et la disparition des morceaux en trop ; un morceau ne s'ecrit
 * qu'avec la fiche qui monte. Un lot incomplet est refuse par le serveur.
 *
 * DEUX VERSIONS, a ne jamais confondre :
 *   - `version` : la REVISION de sauvegarde, +1 a chaque ecriture de la charge.
 *     C'est le verrou optimiste : deux ecritures parties de la meme revision,
 *     la seconde est refusee ;
 *   - `formatVersion` : la version du FORMAT de la charge (celui du moteur qui
 *     l'ecrit). Elle ne descend jamais (rules) : un vieil onglet ne reecrit pas
 *     au vieux format un contenu deja migre. La migration se declare dans le
 *     SDK (`lireAuFormat`, `@taki/sdk`).
 *
 * LES PLAFONDS SONT DES OCTETS UTF-8, pas des caracteres : Firestore mesure un
 * document en octets, et un accent en vaut deux.
 */

/** Plafond d'UN morceau, en octets UTF-8 de la charge stockee (encodee). */
export const CONTENT_PART_MAX = 900_000;
/**
 * Nombre maximum de morceaux d'un contenu. Les rules verifient chaque morceau
 * un par un (`morceauTenu`, sans boucle possible) : ce chiffre est ECRIT DANS
 * LES RULES, et chaque verification y coute un acces document sur les dix
 * permis par operation. Le monter exige de relire ce budget.
 */
export const CONTENT_PARTS_MAX = 8;
/**
 * La charge STOCKEE la plus lourde (7,2 Mo). Stockee veut dire encodee : un
 * JSON compresse (`deflate-base64url`) y tient souvent cinq a dix fois plus gros.
 * Au-dela, l'ecriture est refusee AVANT de partir (`ChargeTropLourde`).
 */
export const CONTENT_CHARGE_MAX = CONTENT_PART_MAX * CONTENT_PARTS_MAX;
/**
 * Plafond de la vignette (`preview`, une data URL). PETIT A DESSEIN : la
 * vignette voyage avec la fiche dans chaque page de galerie. 24 Ko tiennent une
 * image de 320 px en WebP ou JPEG ; cent fiches en pesent 2,4 Mo au pire.
 */
export const CONTENT_PREVIEW_MAX = 24_000;
/** Plafond du nom, en caracteres (ecrit aussi dans les rules). */
export const CONTENT_NAME_MAX = 200;
/** Plafond du type MIME, en caracteres (ecrit aussi dans les rules). */
export const CONTENT_MIME_MAX = 100;
/**
 * Plafond du sac `customFields` d'un contenu, en octets tels que Firestore les
 * compte. Les rules ne savent pas mesurer une map : c'est le SDK qui le tient.
 * Un contenu range sa matiere dans sa charge, pas dans ses `customFields`.
 */
export const CONTENT_CUSTOM_FIELDS_MAX = 8_000;
/** Nombre maximum d'entrees de `recherche` (ecrit aussi dans les rules). */
export const CONTENT_SEARCH_MAX = 200;
/** Longueur maximum d'un prefixe de `recherche`, en caracteres. */
export const CONTENT_SEARCH_PREFIX_MAX = 15;

/**
 * Comment la charge est stockee :
 *   - `utf8`              : le texte tel quel, pour un PETIT texte seulement
 *                           (`SEUIL_EN_CLAIR` du SDK) ;
 *   - `base64url`         : les octets UTF-8 du texte, en base64url ;
 *   - `deflate-base64url` : le texte compresse (deflate), puis en base64url.
 *
 * POURQUOI UN GROS TEXTE N'EST JAMAIS STOCKE EN CLAIR : le poids TRANSPORTE.
 * Le SDK web de Firestore envoie une ecriture en formulaire encode URL : chaque
 * octet hors de l'alphabet sur (lettres, chiffres, `-_.~`) y coute trois
 * octets, et un guillemet de JSON, echappe puis encode, six. Mesure dans la
 * recette du lot (01/10/2026) : 6 Mo d'accents deviennent une requete de 18 Mo,
 * refusee ; le SDK la retente sans fin, la promesse ne se resout jamais, et
 * toutes les ecritures suivantes de l'onglet attendent derriere elle. En
 * base64url, chaque caractere stocke voyage tel quel : le poids transporte est
 * le poids stocke, borne par `CONTENT_CHARGE_MAX`.
 *
 * Le SDK choisit et decode (`sdk.contents.saveContent`, `loadContent`) :
 * l'appelant ne manipule que du texte.
 */
export const CONTENT_ENCODINGS = ["utf8", "base64url", "deflate-base64url"] as const;
export const contentEncodingSchema = z.enum(CONTENT_ENCODINGS);
export type ContentEncoding = z.infer<typeof contentEncodingSchema>;

/**
 * LA TAILLE D'UN TEXTE EN OCTETS UTF-8, sans l'encoder (pure, sans allocation :
 * un `TextEncoder` fabriquerait une copie de la charge pour la mesurer).
 */
export function octetsUtf8(texte: string): number {
  let octets = 0;
  for (let i = 0; i < texte.length; i += 1) {
    const code = texte.charCodeAt(i);
    if (code < 0x80) octets += 1;
    else if (code < 0x800) octets += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < texte.length) {
      const suivant = texte.charCodeAt(i + 1);
      if (suivant >= 0xdc00 && suivant <= 0xdfff) {
        // Une paire de substitution : un seul caractere, quatre octets.
        octets += 4;
        i += 1;
      } else {
        octets += 3;
      }
    } else octets += 3;
  }
  return octets;
}

/**
 * LES MOTS D'UN TEXTE, normalises pour la recherche : sans accents, en
 * minuscules, coupes sur tout ce qui n'est ni lettre ni chiffre, sans doublon,
 * dans l'ordre d'apparition. « Plan de salle (été) » -> plan, de, salle, ete.
 */
export function motsNormalises(texte: string): string[] {
  const mots = texte
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((mot) => mot.length > 0);
  return [...new Set(mots)];
}

/**
 * LA RECHERCHE D'UN CONTENU PAR SON NOM, cote serveur. Firestore ne cherche pas
 * dans un texte ; il sait en revanche trouver une valeur dans un tableau
 * (`array-contains`). On range donc dans `recherche` tous les DEBUTS de mots du
 * nom : « Plan salle » -> p, pl, pla, plan, s, sa, sal, sall, salle. Taper
 * « sal » trouve le contenu, a la bonne page, sans tout charger.
 *
 * Un prefixe est tronque a `CONTENT_SEARCH_PREFIX_MAX` caracteres, et le
 * tableau a `CONTENT_SEARCH_MAX` entrees : au-dela, les derniers mots d'un nom
 * tres long ne se cherchent plus, ce qui est assume (un nom est un titre).
 */
export function prefixesDeRecherche(nom: string): string[] {
  const prefixes: string[] = [];
  const vus = new Set<string>();
  for (const mot of motsNormalises(nom)) {
    const borne = Math.min(mot.length, CONTENT_SEARCH_PREFIX_MAX);
    for (let longueur = 1; longueur <= borne; longueur += 1) {
      const prefixe = mot.slice(0, longueur);
      if (vus.has(prefixe)) continue;
      if (prefixes.length >= CONTENT_SEARCH_MAX) return prefixes;
      vus.add(prefixe);
      prefixes.push(prefixe);
    }
  }
  return prefixes;
}

/** Le plafond de personnes mentionnees dans un contenu, recopie dans les rules. */
export const CONTENT_MENTIONS_MAX = 100;

/**
 * LES MENTIONS D'UN CONTENU (un document de texte) : la projection que
 * l'editeur ecrit apres son enregistrement, `{ personnes, tous, lia }`, meme
 * forme que les mentions d'un message (contrat conversations, section 3 ;
 * `lia` reste faux pour un document). Des `personId`, jamais des noms. Aucune
 * Function ne la lit depuis le retrait de l'app Pages (13/10/2026) : sa
 * notification est archivee dans `docs/archives/apps-retirees-2026-10-13.zip`.
 */
export const contentMentionsSchema = z
  .object({
    personnes: z.array(z.string().min(1)).max(CONTENT_MENTIONS_MAX),
    tous: z.boolean(),
    lia: z.boolean(),
  })
  .strict();
export type ContentMentions = z.infer<typeof contentMentionsSchema>;

/** Champs metier de la fiche d'un contenu (hors champs standard du socle). */
export const contentCoreFields = {
  orgId: z.string(),
  /** Sous-type declare par l'app ('croquis', 'carte-mentale') : la porte que visent les derogations. Immuable. */
  type: z.string().min(1),
  /** Le titre du contenu, affiche dans une galerie ; '' tolere. */
  name: z.string().max(CONTENT_NAME_MAX),
  /** Les debuts de mots du nom (`prefixesDeRecherche`), poses par le SDK. */
  recherche: z.array(z.string()).max(CONTENT_SEARCH_MAX),
  /** 'application/json', 'text/markdown'. Immuable. */
  mimeType: z.string().min(1).max(CONTENT_MIME_MAX),
  /** Comment les morceaux stockent la charge. */
  encoding: contentEncodingSchema,
  /** Octets UTF-8 du texte DECODE (ce que l'utilisateur a produit). */
  size: z.number().int().nonnegative(),
  /** Nombre de morceaux de la charge ; 0 pour un contenu vide. */
  parts: z.number().int().min(0).max(CONTENT_PARTS_MAX),
  /** La REVISION de sauvegarde (verrou optimiste) : 1 a la naissance, +1 par ecriture de la charge. */
  version: z.number().int().positive(),
  /** La version du FORMAT de la charge (celle du moteur) : ne descend jamais. */
  formatVersion: z.number().int().positive(),
  /** Vignette en data URL (PETITE, cf. `CONTENT_PREVIEW_MAX`), ou null. */
  preview: z.string().max(CONTENT_PREVIEW_MAX).nullable(),
  /** L'id du contenu dont celui-ci est la copie (`sdk.contents.copy`), ou null. Immuable. */
  copyOf: z.string().min(1).nullable(),
  /** Les mentions du contenu (`contentMentionsSchema`), posees par l'editeur. Optionnel. */
  mentions: contentMentionsSchema.optional(),
} as const;

/**
 * Schema complet de la fiche d'un contenu (champs standard + champs metier +
 * acces). La famille est PORTEUSE d'un acces par fiche (`acces.ts`) : une page
 * privee ou partagee a quelques personnes porte ses participants ici. Un
 * contenu est equipe ou prive, JAMAIS ancre (`accesContenuSchema`) : la forme
 * ancre ne vaut que pour les conversations.
 */
export const ContentSchema = z.object({
  ...baseEntityFields,
  ...contentCoreFields,
  ...accesContenuField,
});

/** Type de la fiche d'un contenu (sans sa charge). */
export type Content = z.infer<typeof ContentSchema>;

/**
 * Un MORCEAU de charge, `contents/{id}/parts/{i}`. Deux champs, rien d'autre
 * (les rules l'exigent) : l'identite est dans le chemin, les droits et la trace
 * sont ceux de la fiche.
 */
export const ContentPartSchema = z.object({
  /** La revision de la fiche avec laquelle ce morceau a ete ecrit. */
  version: z.number().int().positive(),
  /** Un morceau de la charge stockee, `CONTENT_PART_MAX` octets au plus. */
  payload: z.string().refine((valeur) => octetsUtf8(valeur) <= CONTENT_PART_MAX, {
    message: `payload depasse ${CONTENT_PART_MAX} octets.`,
  }),
});

/** Type d'un morceau de charge. */
export type ContentPart = z.infer<typeof ContentPartSchema>;

/**
 * Ce que l'appelant fournit a la creation. La charge initiale, les revisions,
 * la taille, les morceaux et la recherche sont poses par le SDK
 * (`sdk.contents.create`), jamais fournis.
 */
export const ContentCreateInputSchema = z.object({
  orgId: contentCoreFields.orgId,
  type: contentCoreFields.type,
  name: contentCoreFields.name.default(""),
  mimeType: contentCoreFields.mimeType.default("application/json"),
  formatVersion: contentCoreFields.formatVersion.default(1),
  preview: contentCoreFields.preview.default(null),
  mentions: contentCoreFields.mentions,
  ...accesContenuInputField,
  ...customFieldsField,
});

/** Ce que l'appelant fournit a la creation (les defauts rendent la plupart des champs facultatifs). */
export type ContentCreateInput = z.input<typeof ContentCreateInputSchema>;

/**
 * Le patch de mise a jour : le NOM, la vignette, les MENTIONS et le sac
 * `customFields`, rien d'autre. La charge s'ecrit par `sdk.contents.saveContent`, en un lot
 * avec ses morceaux et la revision attendue ; `orgId`, `type`, `mimeType` et
 * `copyOf` sont immuables (rules).
 */
export const ContentUpdateInputSchema = z
  .object({
    name: contentCoreFields.name,
    preview: contentCoreFields.preview,
    mentions: contentCoreFields.mentions,
    ...customFieldsField,
  })
  .partial();

/** Type du patch de mise a jour d'un contenu. */
export type ContentUpdateInput = z.infer<typeof ContentUpdateInputSchema>;
